import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { type Broker } from 'components/brokers/types'
import BrokerContext from 'components/context/BrokerContext'
import SubstantiationContext from 'components/context/SubstantiationContext'
import {
  getSubstantiationsQueryKey,
  type SubstantiationsFilters,
} from 'components/hooks/substantiations/useGetSubstantiations'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import { Button } from 'slab'
import { type AdminSubstantiation } from 'utils/web_api.schemas'

import BulkUpdateSubstantiationsDialog from './BulkUpdateSubstantiationsDialog'
import SubstantiationFilters, {
  substantiationFilterConfigurations,
  type SubstantiationFilterGroupType,
} from './SubstantiationFilters'
import SubstantiationsQuickFilters from './SubstantiationsQuickFilters'
import SubstantiationsTable, {
  MAX_NUMBER_OF_SELECTED_ITEMS,
} from './SubstantiationsTable'
import SyncFiltersWithURL from './SyncFiltersWithUrl'
import { type User } from './types'

type SubstantiationsViewProps = {
  currentUser: User
  currentBroker: Broker
}

const SubstantiationsViewImpl = ({
  currentUser,
  currentBroker,
}: SubstantiationsViewProps) => {
  const [filters, setFilters] = useState<SubstantiationsFilters>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false)

  // Reset row selection when filters and/or sorting change. This is to prevent the user from bulk updating items no
  // longer visible on the page.
  useEffect(() => {
    setRowSelection({})
  }, [pagination.pageSize, sorting, JSON.stringify(filters)])

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Get selected item IDs from rowSelection state
  const selectedIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id] === true
  )

  // Update selectedItemIds when rowSelection changes
  useEffect(() => {
    setSelectedItemIds(selectedIds)
  }, [rowSelection])

  return (
    <BrokerContext.Provider value={{ currentBroker }}>
      <SubstantiationContext.Provider
        value={{
          setFilters,
          getSubstantiationsQueryKey: getSubstantiationsQueryKey({
            filters,
            pagination,
            sorting,
          }),
        }}
      >
        <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
          <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
            <div className='text-2xl font-semibold w-96'>Substantiations</div>
          </div>
          <SyncFiltersWithURL<
            SubstantiationsFilters,
            AdminSubstantiation,
            SubstantiationFilterGroupType
          >
            filters={filters}
            pagination={pagination}
            setFilters={setFilters}
            defaultFilters={{ review_statuses: ['waiting_for_review'] }}
            sortableKeys={['created_at', 'review_status', 'reviewed_at']}
            filterConfigurations={substantiationFilterConfigurations}
          />
          <SubstantiationsQuickFilters
            filters={filters}
            setFilters={setFilters}
          />
          <SubstantiationFilters
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
          />
          <div className='h-10 mb-2 flex items-center justify-between w-full'>
            <RowSelectionCountDisplay
              numberOfSelectedItems={numberOfSelectedItems}
              setRowSelection={setRowSelection}
              maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
            />
            <Button
              disabled={numberOfSelectedItems === 0}
              onClick={() => setIsBulkUpdateDialogOpen(true)}
              variant='primary'
            >
              Update Selected
            </Button>
          </div>
          <SubstantiationsTable
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            currentUser={currentUser}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
          <BulkUpdateSubstantiationsDialog
            isOpen={isBulkUpdateDialogOpen}
            setIsOpen={setIsBulkUpdateDialogOpen}
            selectedItems={selectedItemIds}
            onSuccess={() => setRowSelection({})}
          />
        </div>
      </SubstantiationContext.Provider>
    </BrokerContext.Provider>
  )
}

const SubstantiationsView = (props: SubstantiationsViewProps) => {
  return (
    <SubstantiationsViewImpl {...props} />
  )
}

export default SubstantiationsView
